/**
 * AuthContext — source unique de vérité pour l'état d'authentification.
 *
 * Le token JWT est stocké dans un cookie httpOnly géré par le backend.
 * JavaScript ne lit jamais le token directement.
 *
 * Flux login :
 *  - sans 2FA : login() → cookies posés → fetchMe()
 *  - avec 2FA : login() retourne `{ totp_required: true, challenge_token }`,
 *               la page /login affiche le formulaire de code, puis
 *               challenge2fa(token, code) finit le login (cookies posés).
 *
 * Au boot :
 *  - appel GET /auth/me avec credentials: "include"
 *  - 200 → user peuplé + organisation récupérée
 *  - 401 → user null (non connecté)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { authApi, type SignupPayload } from "@/lib/api/auth";
import { platformApi, type ImpersonationContext } from "@/lib/api/platform";
import { setImpersonationExpiredHandler, setUnauthorizedHandler } from "@/lib/api/client";
import type { OrganizationRead, UserRead } from "@/lib/api/schema";

/** Result of a `login()` call. `totp_required=true` means the password was
 *  OK and the caller must now POST the code to `challenge2fa(token, code)`. */
export type LoginResult =
  { totp_required: false } | { totp_required: true; challenge_token: string };

interface AuthContextValue {
  user: UserRead | null;
  organization: OrganizationRead | null;
  impersonation: ImpersonationContext | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  signup: (payload: SignupPayload) => Promise<void>;
  challenge2fa: (challenge_token: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  stopImpersonation: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

export function isPublicAuthPath(path: string): boolean {
  return (
    path.startsWith("/login") ||
    path.startsWith("/signup") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    // The e-mail verification link may be opened without a session (another
    // device, an expired session): without this entry the /auth/me probe
    // bounced to /login before the token was consumed.
    path.startsWith("/verifier-email") ||
    path.startsWith("/recueil/") ||
    path.startsWith("/capture/") ||
    path.startsWith("/plateforme")
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserRead | null>(null);
  const [organization, setOrganization] = useState<OrganizationRead | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      try {
        const org = await authApi.organization();
        setOrganization(org);
      } catch {
        setOrganization(null);
      }
      try {
        setImpersonation(await platformApi.currentImpersonation());
      } catch {
        setImpersonation(null);
      }
    } catch {
      setUser(null);
      setOrganization(null);
      setImpersonation(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPublicAuthPath(window.location.pathname)) {
      setUser(null);
      setOrganization(null);
      setImpersonation(null);
      setIsLoading(false);
      return;
    }
    void fetchMe();
  }, [fetchMe]);

  // When the refresh token is also expired, redirect to /login.
  // Skip the redirect on public routes that intentionally render
  // un-authenticated (login flow, password reset flow, public client
  // recueil). Without this, the initial /auth/me probe fired at app
  // mount returns 401 and bounces the user away from the page they
  // just navigated to — e.g. clicking a reset-password email link
  // landed them on /login before the form could render.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setOrganization(null);
      setImpersonation(null);
      const path = window.location.pathname;
      if (!isPublicAuthPath(path)) {
        window.location.href = "/login";
      }
    });
  }, []);

  useEffect(() => {
    setImpersonationExpiredHandler(() => {
      setUser(null);
      setOrganization(null);
      setImpersonation(null);
      window.location.href = "/plateforme?session=expired";
    });
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await authApi.login(email, password);
      if (res.totp_required) {
        // Don't fetchMe yet — the user is not authenticated until they
        // complete the challenge. The login page renders the code form.
        if (!res.challenge_token) {
          throw new Error("Réponse de login invalide : token 2FA manquant");
        }
        return { totp_required: true, challenge_token: res.challenge_token };
      }
      setIsLoading(true);
      await fetchMe();
      return { totp_required: false };
    },
    [fetchMe],
  );

  const signup = useCallback(async (payload: SignupPayload) => {
    const created = await authApi.signup(payload);
    setUser(created.user);
    setOrganization(created.organization);
    setImpersonation(null);
    setIsLoading(false);
  }, []);

  const challenge2fa = useCallback(
    async (challenge_token: string, code: string) => {
      await authApi.challenge2fa(challenge_token, code);
      setIsLoading(true);
      await fetchMe();
    },
    [fetchMe],
  );

  const logout = useCallback(async () => {
    try {
      if (impersonation) {
        await platformApi.stopImpersonation();
      } else {
        await authApi.logout();
      }
    } finally {
      setUser(null);
      setOrganization(null);
      setImpersonation(null);
    }
  }, [impersonation]);

  const stopImpersonation = useCallback(async () => {
    try {
      await platformApi.stopImpersonation();
    } finally {
      setUser(null);
      setOrganization(null);
      setImpersonation(null);
      window.location.href = "/plateforme?session=ended";
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      organization,
      impersonation,
      isLoading,
      isAuthenticated: user !== null,
      login,
      signup,
      challenge2fa,
      logout,
      stopImpersonation,
      refresh: fetchMe,
    }),
    [
      user,
      organization,
      impersonation,
      isLoading,
      login,
      signup,
      challenge2fa,
      logout,
      stopImpersonation,
      fetchMe,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
